import { account, tablesDB, DATABASE_ID, USERS_TABLE_ID } from '../lib/appwrite';
import { ADMIN_ROLES } from '../utils/adminPermissions';

let _cachedUser = null;
let _cachedUserAt = 0;
let _cachedUserRow = null;
let _cachedUserRowAt = 0;
const CACHE_TTL = 30 * 1000;

const isCacheValid = (ts) => ts && Date.now() - ts < CACHE_TTL;

class AdminAuthService {
  static async getCurrentUser() {
    try {
      if (isCacheValid(_cachedUserAt) && _cachedUser) return _cachedUser;
      const user = await account.get();
      _cachedUser = user;
      _cachedUserAt = Date.now();
      return user;
    } catch (error) {
      throw new Error('No user is currently signed in');
    }
  }

  static async getCurrentUserId() {
    const user = await this.getCurrentUser();
    return user.$id;
  }

  static async getUserRow() {
    try {
      if (isCacheValid(_cachedUserRowAt) && _cachedUserRow) return _cachedUserRow;
      const userId = await this.getCurrentUserId();
      const userRow = await tablesDB.getRow(DATABASE_ID, USERS_TABLE_ID, userId);
      _cachedUserRow = userRow;
      _cachedUserRowAt = Date.now();
      return userRow;
    } catch (error) {
      throw new Error('Could not fetch user profile');
    }
  }

  static async getAdminRole() {
    const userRow = await this.getUserRow();
    const raw = (userRow.roles || userRow.role || '').toLowerCase().trim();
    const roles = raw ? raw.split(/[,;|]/).map((r) => r.trim()).filter(Boolean) : [];
    const adminRole = roles.find((r) => ADMIN_ROLES.includes(r));
    return adminRole || 'support';
  }

  static async isAdmin() {
    try {
      const userRow = await this.getUserRow();
      const raw = (userRow.roles || userRow.role || '').toLowerCase().trim();
      if (!raw) return false;
      const roles = raw.split(/[,;|]/).map((r) => r.trim()).filter(Boolean);
      return roles.some((r) => ADMIN_ROLES.includes(r));
    } catch {
      return false;
    }
  }

  static async isAuthenticated() {
    try {
      await this.getCurrentUser();
      const sessions = await account.listSessions();
      if (!sessions?.sessions?.length) return false;
      const admin = await this.isAdmin();
      if (!admin) {
        try {
          await this.deleteAllSessions();
        } catch {
          /* ensure non-admin session is cleared */
        }
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  static async createEmailSession(email, password) {
    const session = await account.createEmailPasswordSession(email, password);
    const userId = session.userId;
    let userRow;
    try {
      userRow = await tablesDB.getRow(DATABASE_ID, USERS_TABLE_ID, userId);
    } catch {
      await account.deleteSession(session.$id);
      throw new Error('Admin access only. Your account is not registered in the admin panel.');
    }
    const raw = (userRow.roles || userRow.role || '').toLowerCase().trim();
    const roles = raw ? raw.split(/[,;|]/).map((r) => r.trim()).filter(Boolean) : [];
    const isAdminRole = roles.some((r) => ADMIN_ROLES.includes(r));
    if (!isAdminRole) {
      await account.deleteSession(session.$id);
      throw new Error('Admin access only. Please use the appropriate app for your account.');
    }
    _cachedUser = null;
    _cachedUserRow = null;
    return session;
  }

  static async deleteSession(sessionId = 'current') {
    await account.deleteSession(sessionId);
  }

  static async deleteAllSessions() {
    const sessions = await account.listSessions();
    await Promise.all(sessions.sessions.map((s) => account.deleteSession(s.$id)));
    _cachedUser = null;
    _cachedUserRow = null;
  }

  static getErrorMessage(error) {
    if (error.type === 'user_invalid_credentials') return 'Invalid email or password.';
    if (error.type === 'user_not_found') return 'No account found with this email.';
    if (error.code === 401) return 'Invalid email or password.';
    return error.message || 'Login failed. Please try again.';
  }

  /** Update current user password. Requires oldPassword to verify. */
  static async updatePassword(oldPassword, newPassword) {
    if (!oldPassword?.trim() || !newPassword?.trim()) {
      throw new Error('Current and new password are required.');
    }
    if (newPassword.length < 8) {
      throw new Error('New password must be at least 8 characters.');
    }
    await account.updatePassword(newPassword, oldPassword);
    _cachedUser = null;
  }

  /** Refresh user row cache (call after profile update) */
  static clearUserCache() {
    _cachedUser = null;
    _cachedUserAt = 0;
    _cachedUserRow = null;
    _cachedUserRowAt = 0;
  }
}

export default AdminAuthService;
